import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Error Boundary para as rotas lazy.
 *
 * ESTUDO — por que precisamos:
 * 1) ChunkLoadError pós-deploy: com code-splitting, cada página vira um "chunk"
 *    com hash no nome. Quando publicamos uma versão nova, os chunks antigos
 *    deixam de existir no servidor. Um usuário com a aba aberta que navegue
 *    para uma página ainda não baixada recebe "Loading chunk failed" (404) e,
 *    SEM boundary, a tela fica branca. Aqui detectamos esse caso e recarregamos
 *    a aba UMA vez (pega os chunks novos) — sem loop.
 * 2) Erro de render de uma página: sem boundary, um erro em qualquer página
 *    derruba o app inteiro (tela branca). Com o boundary DENTRO do layout, o
 *    cabeçalho/menus continuam e mostramos um cartão de erro só na área de
 *    conteúdo, com botão de recarregar.
 */
function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || "";
  const msg = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

export default class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    // Pós-deploy: chunk antigo sumiu → recarrega 1x para buscar os novos.
    if (isChunkLoadError(error)) {
      const KEY = "brave_chunk_reload_ts";
      const last = Number(sessionStorage.getItem(KEY) || 0);
      // evita loop: só recarrega se não recarregou nos últimos 10s
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }

  handleReload = () => window.location.reload();

  render() {
    if (!this.state.hasError) return this.props.children;

    const chunk = isChunkLoadError(this.state.error);
    return (
      <div
        data-testid="route-error-boundary"
        className="min-h-[50vh] flex items-center justify-center p-6"
      >
        <div className="max-w-md w-full text-center rounded-2xl border border-white/10 bg-white/[0.04] p-8">
          <div className="inline-flex p-3 rounded-full bg-amber-500/15 mb-4">
            <AlertTriangle className="w-7 h-7 text-amber-400" />
          </div>
          <h2 className="text-brand-ice font-heading font-bold text-lg mb-1">
            {chunk ? "Nova versão disponível" : "Algo deu errado ao abrir esta página"}
          </h2>
          <p className="text-brand-ice/60 text-sm mb-6">
            {chunk
              ? "O aplicativo foi atualizado. Recarregue para carregar a versão mais recente."
              : "Tente recarregar a página. Se o problema persistir, avise o suporte."}
          </p>
          <button
            data-testid="route-error-reload-btn"
            onClick={this.handleReload}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-light hover:bg-brand-light/90 text-brand-dark font-bold text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Recarregar
          </button>
        </div>
      </div>
    );
  }
}
